import type { FC } from "react";
import { useLocation, useParams } from "react-router-dom";
import { usePortfolioQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioQuery";
import type { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioDetailMenu } from "web_myplann/Portfolio/Components/PortfolioDetailMenu";
import { DetailEmptyState } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailEmptyState";
import { DetailLayout } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailLayout";
import { PORTFOLIO_SECTION_BY_DOMAIN } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/portfolioSectionRegistry";
import { DetailSkeleton } from "web_myplann/Portfolio/Components/Redesign/Detail/Skeletons/DetailSkeleton";
import { hasNoBusinessData } from "web_myplann/Portfolio/Helpers/hasNoBusinessData";
import { portfolioHasChart } from "web_myplann/Portfolio/Helpers/portfolioHasChart";
import { usePortfolioTitle } from "web_myplann/Portfolio/Hooks/usePortfolioTitle";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";

export const PortfolioDetailPageRedesign: FC = () => {
	const { id } = useParams<{ id: string }>();
	const portfolioId = id ? Number(id) : undefined;
	const { t } = useResource();
	const location = useLocation();
	const stateDomain = (location.state as { domain?: ClientPortfolioDomainEnum })
		?.domain;

	const { data: portfolio, isLoading } = usePortfolioQuery(Number(portfolioId));

	const title = usePortfolioTitle(portfolio, t);

	const currencySign =
		portfolio?.currencySign ?? t(Resources.Common.CurrencySign);

	const domain = portfolio?.domain ?? undefined;

	const DomainDetail =
		domain !== undefined ? PORTFOLIO_SECTION_BY_DOMAIN[domain] : undefined;

	return (
		<DetailLayout
			title={title}
			menu={
				domain !== undefined && portfolioId !== undefined ? (
					<PortfolioDetailMenu
						kind={PortfolioItemKind.Portfolio}
						id={portfolioId}
						domain={domain}
						title={title}
					/>
				) : undefined
			}
		>
			{isLoading ? (
				<DetailSkeleton hasChart={portfolioHasChart(stateDomain)} />
			) : hasNoBusinessData(portfolio) ? (
				<DetailEmptyState />
			) : portfolio && DomainDetail ? (
				<DomainDetail baseData={portfolio} currencySign={currencySign} />
			) : null}
		</DetailLayout>
	);
};
